import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Pill, Search, Calendar, User, Package, Hash, RotateCcw, ClipboardList } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { prescriptionService } from '@/services/prescriptionService';
import { Prescription, MedicationRequest } from '@/lib/types';
import { Input } from '../ui/input';
import { useQuery } from '@tanstack/react-query';
import { formatToLocalDate } from '@/lib/utils';
import { medicationUnits } from '@/lib/medication-unit';
import PrescriptionFormSheet from '../clinic/PrescriptionFormSheet';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';

interface PatientMedicationsTabProps {
  patientId: string;
}

interface FlatMedication {
  medication: MedicationRequest;
  prescription: Prescription;
}

const PatientMedicationsTab = ({ patientId }: PatientMedicationsTabProps) => {
  const { isPractitioner } = useClinic();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');

  // Add to Rx Pad state
  const [selectedMedIds, setSelectedMedIds] = useState<Set<string>>(new Set());
  const [isAddToRxPadOpen, setIsAddToRxPadOpen] = useState(false);

  const canUseRxPad = isPractitioner;

  const { data: prescriptionsResponse, isLoading, refetch } = useQuery({
    queryKey: ['patient-medications', patientId],
    queryFn: async () => {
      const response = await prescriptionService.getPrescriptionsByPatientId(patientId, {
        page: 1,
        limit: 200,
        sortBy: 'created_at',
        sortOrder: 'desc',
      });
      return response;
    },
    enabled: !!patientId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const prescriptions: Prescription[] = prescriptionsResponse?.data?.prescriptions || [];

  // Flatten all medications from all prescriptions
  const allMedications: FlatMedication[] = prescriptions.flatMap((prescription) =>
    (prescription.medicationRequests || []).map((med) => ({
      medication: med,
      prescription,
    }))
  );

  // Filter by search
  const filtered = searchTerm
    ? allMedications.filter(
        ({ medication }) =>
          medication.medication?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          medication.dose?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          medication.rx_number?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allMedications;

  const toggleMedSelection = (medId: string) => {
    setSelectedMedIds(prev => {
      const next = new Set(prev);
      if (next.has(medId)) {
        next.delete(medId);
      } else {
        next.add(medId);
      }
      return next;
    });
  };

  const clearMedSelection = () => setSelectedMedIds(new Set());

  const buildPrefillData = () => {
    if (selectedMedIds.size === 0) return undefined;
    const selectedMeds = allMedications
      .filter(({ medication }) => selectedMedIds.has(medication.id))
      .map(({ medication }) => medication);
    if (selectedMeds.length === 0) return undefined;
    return { medicationRequests: selectedMeds, priority: 1 };
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#4A8A9A' }}
              >
                <Pill className="h-4 w-4 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Prescriptions</h3>
                <p className="text-xs text-slate-500">
                  {isLoading ? 'Loading...' : `${filtered.length} medication${filtered.length !== 1 ? 's' : ''}`}
                </p>
              </div>
            </div>

            {/* Search + Actions */}
            <div className="flex items-center gap-2">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search medications..."
                  className="pl-9 h-9 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              {selectedMedIds.size > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearMedSelection}
                    className="text-slate-600 h-9"
                  >
                    Clear ({selectedMedIds.size})
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => setIsAddToRxPadOpen(true)}
                    style={{ backgroundColor: '#4A8A9A' }}
                    className="text-white hover:opacity-90 h-9"
                  >
                    <ClipboardList className="mr-1.5 h-3.5 w-3.5" />
                    Add to Rx Pad
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#4A8A9A]" />
              <span className="ml-2 text-sm text-slate-500">Loading medications...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <Pill className="h-6 w-6 text-slate-400" />
              </div>
              <p className="text-sm text-slate-500">
                {searchTerm ? 'No medications found' : 'No prescriptions yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map(({ medication, prescription }, index) => {
                return (
                  <div
                    key={`${prescription.id}-${medication.id || index}`}
                    className="border border-slate-200 rounded-lg p-4 bg-white hover:border-slate-300 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      {/* Medication Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-3">
                          {canUseRxPad && (
                            <div className="flex items-center self-center flex-shrink-0">
                              <Checkbox
                                checked={selectedMedIds.has(medication.id)}
                                onCheckedChange={() => toggleMedSelection(medication.id)}
                              />
                            </div>
                          )}
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: 'rgba(99, 57, 144, 0.12)' }}
                          >
                            <Package className="h-5 w-5" style={{ color: 'rgb(99, 57, 144)' }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-900 text-sm">
                              {medication.medication}
                              {medication.rx_number && (
                                <span className="text-xs text-blue-600 ml-1.5 font-normal">
                                  (RX: {medication.rx_number})
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                              {medication.dose}{' '}
                              {medicationUnits[medication.unit_of_measure as keyof typeof medicationUnits] ||
                                medication.unit_of_measure}
                              {medication.quantity_authorized && (
                                <span> &middot; Qty: {medication.quantity_authorized}</span>
                              )}
                              {medication.sig && (
                                <span> &middot; Sig: {medication.sig}</span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {prescription.practitioner?.full_name ?? 'Unknown'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatToLocalDate(prescription.created_at, 'MM/DD/YYYY')}
                              </span>
                              {medication.refills_authorized && Number(medication.refills_authorized) > 0 && (
                                <span className="flex items-center gap-1">
                                  <RotateCcw className="h-3 w-3" />
                                  {medication.refills_authorized} refill{Number(medication.refills_authorized) !== 1 ? 's' : ''}
                                </span>
                              )}
                              {medication.day_supply && (
                                <span className="flex items-center gap-1">
                                  <Hash className="h-3 w-3" />
                                  {medication.day_supply} day supply
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Status & Refill indicators */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {prescription.status === 'cancelled' ? (
                          <Badge className="bg-red-100 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full hover:bg-red-100">
                            Cancelled
                          </Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs font-medium px-2.5 py-1 rounded-full hover:bg-emerald-100">
                            Active
                          </Badge>
                        )}
                        {prescription.is_refill_prescription && (
                          <Badge className="bg-orange-50 text-orange-700 border-orange-200 text-xs font-medium px-2.5 py-1 rounded-full border">
                            Refill
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* Add to Rx Pad - Create new order with prefilled medications */}
      <PrescriptionFormSheet
        open={isAddToRxPadOpen}
        onOpenChange={(open) => {
          setIsAddToRxPadOpen(open);
          if (!open) clearMedSelection();
        }}
        mode="create"
        prefillMedications={buildPrefillData()}
        patientId={patientId}
        practitionerId={user?.practitionerId || ''}
        onSubmit={() => {
          refetch();
          clearMedSelection();
        }}
      />
    </>
  );
};

export default PatientMedicationsTab;
